import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import SongOfWeek from "@/components/home/SongOfWeek";
import VinylRecord from "@/components/VinylRecord";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { SHI_SHEET_URL } from "@/lib/images";

function ArchiveRecord({ song }) {
  const listenUrl = song.listen_url || song.external_listen_url;
  return (
    <div
      data-testid={`song-archive-item-${song.id}`}
      className="flex gap-5 rounded-2xl border border-corduroy bg-cocoa p-5 md:p-6"
    >
      {song.artwork_url ? (
        <img
          src={song.artwork_url}
          alt={`${song.title} — ${song.artist}`}
          className="h-20 w-20 shrink-0 rounded-full border border-gold/30 object-cover md:h-24 md:w-24"
          loading="lazy"
        />
      ) : (
        <VinylRecord size={80} spin={false} label="TWSS" />
      )}
      <div className="min-w-0">
        <h3 className="truncate font-display text-xl font-bold text-cream md:text-2xl">
          &ldquo;{song.title}&rdquo;
        </h3>
        <p className="font-editorial italic text-gold">{song.artist}</p>
        {song.note && (
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-latte">
            {song.note}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {listenUrl && (
            <a
              href={listenUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-sans text-[10px] font-bold uppercase tracking-[0.22em] text-ember transition-opacity hover:opacity-70"
            >
              <ExternalLink size={13} /> Listen
            </a>
          )}
          {song.related_program && (
            <Link
              to={`/episodes/${song.related_program}`}
              className="font-sans text-[10px] font-bold uppercase tracking-[0.22em] text-cream/60 transition-colors hover:text-gold"
            >
              The Program &rarr;
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MusicPage() {
  const [songs, setSongs] = useState(null);

  useEffect(() => {
    api
      .get("/song-of-the-week/archive")
      .then((r) => setSongs(r.songs || []))
      .catch(() => setSongs([]));
  }, []);

  // The newest pick is the featured listening station; the rest fill the box.
  const rest = songs ? songs.slice(1) : [];

  return (
    <div data-testid="music-page">
      <Seo title="Music | That’s What SHi Said" description="Music from Ashyla, the artist behind That’s What SHi Said." path="/music" />
      <div className="mx-auto max-w-7xl px-5 pt-32 md:px-10 md:pt-40">
        <Reveal>
          <Overline>Music</Overline>
          <h1 className="mt-4 font-display text-5xl font-black leading-[0.9] tracking-tight md:text-7xl">
            The <span className="italic text-gold">Music.</span>
          </h1>
          <p className="mt-6 max-w-2xl font-editorial text-xl italic leading-relaxed text-cream/80 md:text-2xl">
            I&rsquo;ve been making music longer than I&rsquo;ve been making this
            show. You&rsquo;ll find it here when it&rsquo;s ready.
          </p>
        </Reveal>
      </div>

      {/* Featured / current pick as the full listening station */}
      <div className="mt-16">
        <SongOfWeek />
      </div>

      {/* The Record Box — full archive */}
      <div id="record-box" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-28">
        <Reveal>
          <Overline>The Record Box</Overline>
          <h2 className="mt-4 font-display text-3xl font-black tracking-tight md:text-5xl">
            Every pick, on the shelf.
          </h2>
        </Reveal>

        {songs && rest.length > 0 ? (
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {rest.map((song, i) => (
              <Reveal key={song.id} delay={0.05 * i}>
                <ArchiveRecord song={song} />
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal delay={0.1}>
            <div
              data-testid="music-archive-empty"
              className="mt-12 rounded-3xl border border-gold/25 bg-cocoa p-10 md:p-14"
            >
              <p className="font-editorial italic text-2xl text-gold md:text-3xl">
                Nothing in the box yet.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-latte md:text-lg">
                When there&rsquo;s a track I want you to hear, it lands here
                &mdash; artwork, a note, and where to listen.
              </p>
              <a href={SHI_SHEET_URL} target="_blank" rel="noopener" data-testid="music-shi-sheet-button" className="mt-8 inline-flex rounded-full border border-gold bg-neon px-7 py-3.5 font-bold text-ink transition-colors hover:bg-cream">Get The Shi Sheet</a>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
